import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Popconfirm,
  MessagePlugin,
} from 'tdesign-react';
import type { PrimaryTableCol, PageInfo } from 'tdesign-react';
import { SearchIcon, RefreshIcon } from 'tdesign-icons-react';
import {
  SUB_CATEGORY_LABEL,
  SubCategory,
  deleteWecomChat,
  fetchWecomChatList,
  updateWecomChat,
  type WecomChat,
} from '@/services/wecom-chat';
import { formatDateTime } from '@/utils/datetime';
import WecomChatFormDialog, { type WecomChatFormValue } from './WecomChatFormDialog';
import './WecomChatList.css';

const CHAT_TYPE_OPTIONS = [
  { label: '全部', value: '' },
  { label: '群聊', value: 'group' },
  { label: '单聊', value: 'single' },
];

const SUB_CATEGORY_FILTER_OPTIONS = [
  { label: '全部', value: '' as const },
  { label: '未绑定', value: 0 as const },
  ...Object.entries(SUB_CATEGORY_LABEL).map(([v, label]) => ({
    label,
    value: Number(v) as SubCategory,
  })),
];

const renderSubCategoryTag = (sub: number | null) => {
  if (!sub) return <Tag theme="default">未绑定</Tag>;
  const label = SUB_CATEGORY_LABEL[sub as SubCategory];
  return <Tag theme="primary" variant="light">{label ?? `未知(${sub})`}</Tag>;
};

const WecomChatList = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<WecomChat[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [chatTypeFilter, setChatTypeFilter] = useState<string>('');
  // 0 表示「未绑定」筛选；'' 表示全部
  const [subCategoryFilter, setSubCategoryFilter] = useState<number | ''>('');

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editing, setEditing] = useState<WecomChat | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWecomChatList({
        page,
        pageSize,
        keyword: searchKeyword,
        chatType: chatTypeFilter ? (chatTypeFilter as 'group' | 'single') : undefined,
        // 0 用来代表"未绑定"，后端不识别 0 → 这里改用 undefined 让后端忽略，
        // 然后在前端 list 过滤"未绑定"也行；为简化，未绑定也走后端：传 subCategory=0
        subCategory: subCategoryFilter === '' ? undefined : subCategoryFilter,
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchKeyword, chatTypeFilter, subCategoryFilter]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleSearch = () => {
    setPage(1);
    setSearchKeyword(keyword.trim());
  };

  const handleReset = () => {
    setKeyword('');
    setChatTypeFilter('');
    setSubCategoryFilter('');
    setPage(1);
    setSearchKeyword('');
    // 即使过滤条件没有发生变化（loadList 依赖未变 → useEffect 不会再触发），
    // 也强制刷新一次，保证用户点击「重置」始终有反馈。
    loadList();
  };

  const handleOpenEdit = (record: WecomChat) => {
    setEditing(record);
    setEditDialogVisible(true);
  };

  const handleEditSubmit = async (payload: WecomChatFormValue) => {
    if (!editing) return;
    setEditSubmitting(true);
    try {
      await updateWecomChat({
        id: editing.id,
        // 空串 → null（清空）；undefined 不传后端保留原值，这里始终传，行为更直观
        name: payload.name === '' ? null : payload.name ?? null,
        subCategory: payload.subCategory ?? null,
        remark: payload.remark === '' ? null : payload.remark ?? null,
      });
      MessagePlugin.success('更新成功');
      setEditDialogVisible(false);
      setEditing(null);
      loadList();
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (record: WecomChat) => {
    try {
      await deleteWecomChat(record.id);
      MessagePlugin.success('删除成功');
      if (list.length === 1 && page > 1) setPage(page - 1);
      else loadList();
    } catch {
      /* 拦截器统一提示 */
    }
  };

  const columns = useMemo<PrimaryTableCol<WecomChat>[]>(
    () => [
      { colKey: 'id', title: 'ID', width: 70 },
      {
        colKey: 'chatType',
        title: '类型',
        width: 80,
        cell: ({ row }) => (row.chatType === 'group' ? '群聊' : row.chatType === 'single' ? '单聊' : row.chatType),
      },
      {
        colKey: 'name',
        title: '群聊名称',
        width: 180,
        cell: ({ row }) => row.name || <span style={{ color: '#999' }}>未填写</span>,
      },
      {
        colKey: 'subCategory',
        title: '绑定分类',
        width: 120,
        cell: ({ row }) => renderSubCategoryTag(row.subCategory),
      },
      {
        colKey: 'chatid',
        title: 'ChatID',
        width: 260,
        cell: ({ row }) => <div className="wecom-chat-chatid">{row.chatid}</div>,
      },
      {
        colKey: 'lastFrom',
        title: '最近发送人',
        width: 140,
        cell: ({ row }) => row.lastFrom || '-',
      },
      {
        colKey: 'lastMsgAt',
        title: '最近消息时间',
        width: 170,
        cell: ({ row }) => formatDateTime(row.lastMsgAt),
      },
      {
        colKey: 'remark',
        title: '备注',
        width: 160,
        cell: ({ row }) => row.remark || '-',
      },
      {
        colKey: 'operation',
        title: '操作',
        fixed: 'right',
        width: 140,
        cell: ({ row }) => (
          <Space size="small">
            <Button theme="primary" variant="text" onClick={() => handleOpenEdit(row)}>
              编辑
            </Button>
            <Popconfirm
              content={`确定删除群聊「${row.name || row.chatid}」吗？`}
              onConfirm={() => handleDelete(row)}
            >
              <Button theme="danger" variant="text">
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, page],
  );

  const handlePageChange = (info: PageInfo) => {
    setPage(info.current);
    setPageSize(info.pageSize);
  };

  return (
    <div className="wecom-chat-list-page">
      <div className="wecom-chat-list-toolbar">
        <Space>
          <Input
            value={keyword}
            onChange={(val) => setKeyword(val as string)}
            placeholder="搜索 chatid / 群名 / 备注 / 发送人"
            clearable
            style={{ width: 320 }}
            onEnter={handleSearch}
          />
          <Select
            value={chatTypeFilter}
            onChange={(v) => {
              setChatTypeFilter((v as string) ?? '');
              setPage(1);
            }}
            options={CHAT_TYPE_OPTIONS}
            style={{ width: 120 }}
            placeholder="类型"
          />
          <Select
            value={subCategoryFilter}
            onChange={(v) => {
              setSubCategoryFilter((v as number | '') ?? '');
              setPage(1);
            }}
            options={SUB_CATEGORY_FILTER_OPTIONS}
            style={{ width: 160 }}
            placeholder="绑定分类"
          />
          <Button icon={<SearchIcon />} onClick={handleSearch}>
            搜索
          </Button>
          <Button variant="outline" icon={<RefreshIcon />} onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        data={list}
        columns={columns}
        loading={loading}
        bordered
        stripe
        size="medium"
        pagination={{
          current: page,
          pageSize,
          total,
          showJumper: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handlePageChange,
        }}
      />

      <WecomChatFormDialog
        visible={editDialogVisible}
        loading={editSubmitting}
        initialValue={editing}
        onCancel={() => {
          setEditDialogVisible(false);
          setEditing(null);
        }}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default WecomChatList;
